/**
 * Customized to optionally pass `widgetContainerSx` down to widgets.
 *
 * This customization supports espots that require styles to be applied
 * at the widget container level (for example, enforcing `width: 100%`), where
 * those styles cannot be derived from the widget itself.
 *
 * Widgets that define or implicitly control their own container styles should
 * not rely on this behavior.
 *
 * Examples:
 * - TripleStack and DoubleStack layouts show how `widgetContainerSx` is passed
 *   to widgets.
 * - The ContentRecommendation widget shows how `widgetContainerSx` is applied
 *   to the widget container.
 */

import { contentManifest } from '@/components/content/manifest';
import { PreviewWidgetFrame } from '@/components/preview/PreviewWidgetFrame';
import { ContentItem } from '@/data/types/LayoutAllSlots';
import { WidgetComponentProps } from '@/data/types/Slot';
import { SxProps, Theme } from '@mui/material';
import { FC } from 'react';

type Props = {
	slot?: ContentItem[];
	SlotWrap?: FC;
	ItemWrap?: FC;
	widgetContainerSx?: SxProps<Theme>;
};

const NullElement: FC = () => null;

const FragmentWrap: FC<{ children?: JSX.Element | JSX.Element[] }> = ({ children }) => (
	<>{children}</>
);

export const LayoutSlot: FC<Props> = ({
	slot,
	SlotWrap = FragmentWrap,
	ItemWrap = FragmentWrap,
	widgetContainerSx,
}) =>
	slot ? (
		<SlotWrap>
			{slot.map((contentItem) => {
				const { name, id, properties, widgetRaw } = contentItem;
				const Element = contentManifest[name] as
					| React.ComponentType<WidgetComponentProps>
					| undefined;

				return Element ? (
					widgetRaw ? (
						<ItemWrap key={name + id + properties?.cid}>
							<PreviewWidgetFrame widget={{ ...widgetRaw, dataId: id }}>
								<Element id={id} properties={properties} sx={widgetContainerSx} />
							</PreviewWidgetFrame>
						</ItemWrap>
					) : (
						<ItemWrap key={name + id + properties?.cid}>
							<Element id={id} properties={properties} />
						</ItemWrap>
					)
				) : (
					<NullElement key={name + id + properties?.cid} />
				);
			})}
		</SlotWrap>
	) : null;
